import React, { useEffect, useState } from 'react';
import { StateManager } from '../managers/StateManager';
import { ContextMenuHandlers } from '../hooks/pixi/types';
import { buildWireContextMenu } from '../hooks/pixi/wireMenu';
import { COLORS } from '../utils/constants';
import { formatGauge, formatLength } from '../utils/length';
import { SidePanel } from './SidePanel';

interface WirePanelProps {
  stateManager: StateManager;
  contextMenuHandlers: ContextMenuHandlers;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  width: number;
  onWidthChange: (width: number) => void;
}

/**
 * Right-side panel listing every wire in the harness. Hovering a row highlights
 * the wire's route on the canvas; right-clicking a row opens the same context
 * menu as the connector wire rows (edit, assign pins, delete, highlight).
 */
export const WirePanel: React.FC<WirePanelProps> = ({
  stateManager,
  contextMenuHandlers,
  collapsed,
  onToggleCollapsed,
  width,
  onWidthChange
}) => {
  const [state, setState] = useState(stateManager.getState());
  const [highlightedWireId, setHighlightedWireId] = useState(stateManager.getInteractionState().highlightedWireId);

  useEffect(() => {
    const update = () => setState(stateManager.getState());
    update();
    return stateManager.subscribe(update);
  }, [stateManager]);

  useEffect(() => {
    const update = () => setHighlightedWireId(stateManager.getInteractionState().highlightedWireId);
    update();
    return stateManager.subscribeToInteractions(update);
  }, [stateManager]);

  // Leave no highlight behind when the panel is closed or unmounted.
  useEffect(() => () => stateManager.setHighlightedWire(undefined), [stateManager]);

  const connectorName = (id: string): string =>
    state.connectors.find(c => c.id === id)?.name ?? 'Unknown';

  const pinLabel = (connectorId: string, pinId?: string): string | undefined => {
    if (!pinId) {
      return undefined;
    }
    return state.connectors.find(c => c.id === connectorId)?.pins?.find(p => p.id === pinId)?.label;
  };

  const endpoint = (connectorId: string, pinId?: string): string => {
    const pin = pinLabel(connectorId, pinId);
    return pin ? `${connectorName(connectorId)} [${pin}]` : connectorName(connectorId);
  };

  // A single colour, or a diagonal stripe of the second colour when the two differ.
  const swatchBackground = (color: string, stripe?: string): string => {
    if (stripe && stripe !== color) {
      return `linear-gradient(135deg, ${color} 0%, ${color} 44%, ${stripe} 44%, ${stripe} 56%, ${color} 56%, ${color} 100%)`;
    }
    return color;
  };

  const openRowMenu = (wireId: string, e: React.MouseEvent): void => {
    e.preventDefault();
    const items = buildWireContextMenu({ stateManager, contextMenuHandlers }, wireId, e.clientX, e.clientY);
    if (items.length > 0) {
      contextMenuHandlers.showContextMenu(e.clientX, e.clientY, items);
    }
  };

  return (
    <SidePanel
      side="right"
      title="Wires"
      count={state.wires.length}
      collapsed={collapsed}
      onToggleCollapsed={onToggleCollapsed}
      width={width}
      onWidthChange={onWidthChange}
    >
      <div style={{ overflowY: 'auto', flex: 1 }}>
        {state.wires.length === 0 && (
          <div style={{ padding: '16px', color: '#b2bec3', fontSize: '13px' }}>
            No wires yet. Drag from a connector to create one.
          </div>
        )}

        {state.wires.map(wire => {
          const total = stateManager.getWireLength(wire.id);
          const active = wire.id === highlightedWireId;
          return (
            <div
              key={wire.id}
              onMouseEnter={() => stateManager.setHighlightedWire(wire.id)}
              onMouseLeave={() => stateManager.setHighlightedWire(undefined)}
              onDoubleClick={() => contextMenuHandlers.handleWireEdit(wire.id)}
              onContextMenu={(e) => openRowMenu(wire.id, e)}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px',
                padding: '10px 16px',
                borderBottom: '1px solid #f4f4f4',
                cursor: 'pointer',
                backgroundColor: active ? '#fffbea' : 'white'
              }}
            >
              <span style={{
                flex: '0 0 auto',
                width: '12px',
                height: '12px',
                marginTop: '3px',
                borderRadius: '3px',
                border: '1px solid #ccc',
                background: swatchBackground(wire.color ?? COLORS.WIRE_BUNDLE, wire.stripeColor)
              }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                  <span style={{ fontSize: '14px', color: '#2d3436', fontWeight: 600 }}>{wire.name}</span>
                  {wire.gauge !== undefined && (
                    <span style={{ fontSize: '12px', color: '#95a5a6' }}>{formatGauge(wire.gauge)}</span>
                  )}
                </div>
                <div style={{ fontSize: '12px', color: '#636e72', marginTop: '2px' }}>
                  {endpoint(wire.startConnectorId, wire.startPinId)} → {endpoint(wire.endConnectorId, wire.endPinId)}
                </div>
                {wire.note && (
                  <div style={{ fontSize: '12px', color: '#b2bec3', marginTop: '2px' }}>{wire.note}</div>
                )}
              </div>
              <span style={{ flex: '0 0 auto', fontSize: '12px', color: total > 0 ? '#2d3436' : '#b2bec3', fontWeight: 600 }}>
                {total > 0 ? formatLength(total) : '-'}
              </span>
            </div>
          );
        })}
      </div>
    </SidePanel>
  );
};
